import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { getNextCalcNumber } from '../../../services/dataService';
import { useAuth } from '../../../contexts/AuthContext';

const SaveEstimateModal = ({ isOpen, onClose, onSave, estimateData, currency, editingEstimate }) => {
  const { user } = useAuth();
  const [ptcName, setPtcName] = useState('');
  const [ptcNumber, setPtcNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [calcNumber, setCalcNumber] = useState('Calc-001');
  const [loadingCalcNum, setLoadingCalcNum] = useState(false);
  const [errors, setErrors] = useState({});

  const isEditMode = !!editingEstimate;

  useEffect(() => {
    if (isOpen && user) {
      if (isEditMode) {
        // Pre-fill fields from the existing estimate
        setPtcName(editingEstimate?.name || '');
        setPtcNumber(editingEstimate?.ptcNumber || '');
        setNotes(editingEstimate?.notes || '');
        setCalcNumber(editingEstimate?.calcNumber || '');
      } else {
        setPtcName('');
        setPtcNumber('');
        setNotes('');
        setLoadingCalcNum(true);
        getNextCalcNumber()?.then(num => setCalcNumber(num))?.catch(() => setCalcNumber('Calc-001'))?.finally(() => setLoadingCalcNum(false));
      }
      setErrors({});
    }
  }, [isOpen, user, isEditMode, editingEstimate]);

  const validate = () => {
    const newErrors = {};
    if (!ptcName?.trim()) newErrors.ptcName = 'PTC Name is required';
    if (!ptcNumber?.trim()) newErrors.ptcNumber = 'PTC Number is required';
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;

    onSave({
      name: ptcName,
      calcNumber: calcNumber,
      ptcNumber: ptcNumber?.trim(),
      notes: notes,
      ...estimateData
    });

    setPtcName('');
    setPtcNumber('');
    setNotes('');
    setErrors({});
    onClose();
  };

  const currencySymbol = currency?.symbol || 'OMR';

  const formatCurrency = (value) => {
    return `OMR ${(parseFloat(value) || 0)?.toFixed(3)}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
      <div className="bg-card border border-border rounded-2xl shadow-2xl max-w-md w-full animate-scale-in">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-primary to-accent shadow-lg">
              <Icon name={isEditMode ? 'Pencil' : 'Save'} size={20} color="white" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">{isEditMode ? 'Update Estimate' : 'Save Estimate'}</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-smooth"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-center gap-3 p-3 bg-primary/10 border border-primary/20 rounded-lg">
            <Icon name="Hash" size={16} color="var(--color-primary)" />
            <div className="flex-1">
              <div className="text-xs text-muted-foreground">Reference Number</div>
              <div className="text-sm font-bold text-primary">
                {loadingCalcNum ? 'Generating...' : calcNumber}
              </div>
            </div>
            <span className="text-xs text-muted-foreground italic">{isEditMode ? 'Existing' : 'Auto-assigned'}</span>
          </div>

          <div className="space-y-1">
            <Input
              label="PTC Name *"
              placeholder="e.g., Q1 2026 Web Development"
              value={ptcName}
              onChange={(e) => { setPtcName(e?.target?.value); setErrors(prev => ({ ...prev, ptcName: '' })); }}
              required
            />
            {errors?.ptcName && <p className="text-xs text-error pl-1">{errors?.ptcName}</p>}
          </div>

          <div className="space-y-1">
            <Input
              label="PTC Number *"
              placeholder="e.g., CRM-2026-0042"
              value={ptcNumber}
              onChange={(e) => { setPtcNumber(e?.target?.value); setErrors(prev => ({ ...prev, ptcNumber: '' })); }}
              required
            />
            {errors?.ptcNumber && <p className="text-xs text-error pl-1">{errors?.ptcNumber}</p>}
            <p className="text-xs text-muted-foreground pl-1">CRM reference code for this estimate</p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Notes (Optional)</label>
            <textarea
              placeholder="Add any additional notes or context..."
              value={notes}
              onChange={(e) => setNotes(e?.target?.value)}
              rows={3}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-smooth resize-none"
            />
          </div>

          <div className="p-4 bg-gradient-to-br from-primary/10 to-accent/10 border border-primary/30 rounded-xl">
            <div className="text-xs font-medium text-muted-foreground mb-3">Estimate Summary</div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Cost:</span>
                <span className="font-medium text-foreground">{formatCurrency(estimateData?.calculations?.totalCost)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Sell Price:</span>
                <span className="font-bold text-primary">{formatCurrency(estimateData?.calculations?.sellPrice)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Profit Margin:</span>
                <span className="font-medium text-success">{estimateData?.calculations?.profitMarginPercentage?.toFixed(2)}%</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="default"
            iconName={isEditMode ? 'RefreshCw' : 'Save'}
            iconPosition="left"
            onClick={handleSave}
            disabled={loadingCalcNum}
          >
            {isEditMode ? 'Update Estimate' : 'Save Estimate'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SaveEstimateModal;